<template>
    <div ref="startForm" :style="{'scroll-margin-top':headerHeight}">
        <div class="card p-3 mb-3">
            <div class="row g-3">
                <div class="col-md-3 select_filter" v-if="dataSetOptions.length">
                    <label class="form-label">Data Sets</label>
                    <select class="form-select" v-model="filters.data_set_id">
                        <option v-for="o in dataSetOptions" :key="o.id" :value="o.id">
                            {{ o.name }}
                        </option>
                    </select>
                </div>

                <div class="col-md-3" v-if="availableCriteria.last_name">
                    <label class="form-label">Last Name</label>
                    <input class="form-control" v-model="filters.last_name" placeholder="Type Last Name..."/>
                </div>

                <div class="col-md-3" v-if="availableCriteria.given_name">
                    <label class="form-label">Given Names</label>
                    <input class="form-control" v-model="filters.given_name" placeholder="Type given name..."/>
                </div>

                <div class="col-md-3 select_filter" v-if="locationCriteriaOptions.length">
                    <label class="form-label">Location by</label>
                    <select class="form-select" v-model="filters.location.criteria">
                        <option v-for="o in locationCriteriaOptions" :key="o.value" :value="o.value">
                            {{ o.text }}
                        </option>
                    </select>
                </div>

                <div class="col-md-3" v-if="locationCriteriaOptions.length">
                    <label class="form-label">Location Search</label>
                    <input class="form-control" v-model="filters.location.term" placeholder="Location Search..."/>
                </div>

                <div class="col-md-3 select_filter" v-if="dateCriteriaOptions.length">
                    <label class="form-label">Date by</label>
                    <select class="form-select" v-model="filters.date.criteria">
                        <option v-for="o in dateCriteriaOptions" :key="o.value" :value="o.value">
                            {{ o.text }}
                        </option>
                    </select>
                </div>

                <div class="col-md-3" v-if="dateCriteriaOptions.length">
                    <label class="form-label">Date Search</label>
                    <input class="form-control" v-model="filters.date.term" placeholder="Date Search..."/>
                </div>

                <div class="col-md-3 d-flex align-items-end gap-2">
                    <button class="btn btn-primary py-1 px-5" :disabled="isLoading" @click="applyFiltersNow"><i class="fas fa-search" title="Search"></i></button>
                    <button class="btn btn-secondary py-1 ml-3 px-5" :disabled="isLoading" @click="clearFilters"><i class="fas fa-times" title="Clear"></i></button>
                </div>
            </div>
        </div>

        <div class="table-responsive mt-3">
            <table class="table table-bordered table-striped udm-table">
                <thead>
                <tr>
                    <th>Name</th>
                    <th>Date(s)</th>
                    <th>Location</th>
                    <th>Data Set</th>
                    <th style="width: 60px;"></th>
                </tr>
                </thead>

                <tbody>
                <tr
                    v-for="row in records"
                    :key="row.id"
                    class="udm-row-click"
                    @click="goToRecord(row)"
                >
                    <td v-html="row.hit_name || '—'"></td>
                    <td v-html="row.hit_date || '—'"></td>
                    <td v-html="row.hit_location || '—'"></td>
                    <td :title="row.data_set_name || '—'">{{ row.data_set_name || '—' }}</td>

                    <td class="udm-eye-cell">
                        <i class="fas fa-eye" title="Show"></i>
                    </td>
                </tr>

                <tr v-if="!isLoading && records.length === 0">
                    <td colspan="5" class="text-center fw-bold py-3">No Records Found</td>
                </tr>

                <tr v-if="isLoading">
                    <td colspan="5" class="text-center py-3">Loading...</td>
                </tr>
                </tbody>
            </table>
        </div>
        <Pagination
            :options.sync="options"
            :total-items="total"
            @change="loadData({scroll: true})"
        />
    </div>
</template>

<script>
import Pagination from "./public/Pagination.vue";
import { headerOffset } from "../mixins/headerOffset";

export default {
    name: "DataSetComponent",
    components: {Pagination},
    mixins: [headerOffset],
    props: {
        dataSetId: {
            type: [Number, null],
            required: false,
            default: null,
        },
    },
    data: () => ({
        restoring: true,
        isLoading: false,
        records: [],
        total: 0,
        options: {
            filters: {
                data_set_id: null,
                last_name: "",
                given_name: "",
                location: {criteria: null, term: ""},
                date: {criteria: null, term: ""},
            },
        },
        availableCriteria: {
            last_name: false,
            given_name: false,
        },
        locationCriteriaOptions: [],
        dateCriteriaOptions: [],
        dataSetOptions: [],
        _debounceTimer: null,
        debouncedApply: null,
    }),
    computed: {
        filters() {
            return this.options.filters;
        },
        activeDataSetIds() {
            return this.filters.data_set_id
                ? [this.filters.data_set_id]
                : (this.dataSetIds?.length ? this.dataSetIds : []);
        },
        backUrl() {
            return typeof window !== 'undefined' ? window.location.href : '';
        },
        filtersSignature() {
            return JSON.stringify(this.filters);
        },
    },
    watch: {
        filtersSignature() {
            if (this.restoring) return;
            this.debouncedApply();
        },
        async "filters.data_set_id"() {
            if (this.restoring) return;

            clearTimeout(this._debounceTimer);
            this.resetSearchFilters();

            this.options.page = 1;

            await this.loadSearchCriteria();
            await this.$nextTick();

            clearTimeout(this._debounceTimer);
            await this.loadData();
        }
    },
    async mounted() {
        this.debouncedApply = this.debounce(() => {
            this.options.page = 1;
            this.loadData();
        }, 1000);

        this.$query.bind(this, {
            namespace: 'udm',
        });

        await this.loadDataSets();
        await this.loadSearchCriteria();

        this.restoring = false;
        await this.loadData();
    },
    beforeDestroy() {
        clearTimeout(this._debounceTimer);
    },
    methods: {
        debounce(fn, wait = 400) {
            return (...args) => {
                clearTimeout(this._debounceTimer);
                this._debounceTimer = setTimeout(() => fn(...args), wait);
            };
        },
        resetSearchFilters() {
            this.filters.last_name = "";
            this.filters.given_name = "";
            this.filters.location = { criteria: null, term: "" };
            this.filters.date = { criteria: null, term: "" };
        },
        buildSearchObject() {
            const s = (v) => String(v || "").trim();
            const search = {};

            if (s(this.filters.last_name)) search.last_name = s(this.filters.last_name);
            if (s(this.filters.given_name)) search.given_name = s(this.filters.given_name);

            if (s(this.filters.location.term) && this.filters.location.criteria) {
                search[this.filters.location.criteria] = s(this.filters.location.term);
            }

            if (s(this.filters.date.term) && this.filters.date.criteria) {
                search[this.filters.date.criteria] = s(this.filters.date.term);
            }

            return search;
        },
        serialize(params) {
            const sp = new URLSearchParams();

            if (params.page != null) sp.set("page", String(params.page));
            if (params.itemsPerPage != null) sp.set("itemsPerPage", String(params.itemsPerPage));
            if (params.search != null) sp.set("search", JSON.stringify(params.search));

            (params.data_set_ids || []).forEach(id => sp.append("data_set_ids[]", String(id)));
            (params.ids || []).forEach(id => sp.append("ids[]", String(id)));

            return sp.toString();
        },
        applyFiltersNow() {
            this.options.page = 1;
            this.loadData();
        },
        clearFilters() {
            this.filters.data_set_id = null;
            this.resetSearchFilters();

            this.filters.location.criteria = this.locationCriteriaOptions[0]?.value || null;
            this.filters.date.criteria = this.dateCriteriaOptions[0]?.value || null;

            this.options.page = 1;
            this.loadData();
        },
        async loadDataSets() {
            const { data } = await this.$api.get(`api/v1/udm/data-sets`, {
                params: { ids: this.dataSetIds || [] },
                paramsSerializer: this.serialize,
            });

            this.dataSetOptions = data?.data || data || [];
        },
        async loadSearchCriteria() {
            const { data } = await this.$api.get(`api/v1/udm/records/search-criteria`, {
                params: { data_set_ids: this.activeDataSetIds },
                paramsSerializer: this.serialize,
            });

            this.availableCriteria.last_name = !!data?.last_name;
            this.availableCriteria.given_name = !!data?.given_name;
            this.locationCriteriaOptions = data?.location || [];
            this.dateCriteriaOptions = data?.date || [];

            if (!this.filters.location.criteria) {
                this.filters.location.criteria =
                    this.locationCriteriaOptions[0]?.value || null;
            }

            if (!this.filters.date.criteria) {
                this.filters.date.criteria =
                    this.dateCriteriaOptions[0]?.value || null;
            }
        },
        async loadData({ scroll = false } = {}) {
            this.isLoading = true;

            try {
                const params = {
                    page: this.options.page,
                    itemsPerPage: this.options.itemsPerPage,
                    search: this.buildSearchObject(),
                    data_set_ids: this.activeDataSetIds,
                };

                const { data } = await this.$api.get(`api/v1/udm/records`, {params, paramsSerializer: this.serialize});

                this.records = data.data || [];
                this.total = data?.meta?.total || 0;
            } finally {
                this.isLoading = false;
                if (scroll) this.$nextTick(() => this.$refs.startForm?.scrollIntoView({ behavior: "smooth" }));
            }
        },
        goToRecord(row) {
            window.location.href =
                `/udm/data-sets/${row.data_set_id}/records/${row.id}?back=${encodeURIComponent(this.backUrl)}`;
        }
    },
};
</script>

<style scoped>
.table-responsive {
    background: #f6f6f6;
}

.udm-table thead {
    border-bottom: 3px solid #c6c6c6;
}

.udm-table th {
    font-weight: 600;
}

.udm-table td {
    vertical-align: middle;
    text-align: left;
}

.fa-eye {
    color: #0b100b;
    cursor: pointer;
}

.fa-eye:hover {
    color: #0f192a;
}

.select_filter select {
    display: block;
    width: 100%;
    height: 38px;
}

.udm-table {
    table-layout: fixed;
    width: 100%;
    min-width: 600px;
}

.udm-table th,
.udm-table td {
    overflow: hidden;
}

.udm-table th:nth-child(1),
.udm-table td:nth-child(1) {
    width: 30%;
}

.udm-table th:nth-child(2),
.udm-table td:nth-child(2) {
    width: 20%;
}

.udm-table th:nth-child(3),
.udm-table td:nth-child(3) {
    width: auto;
}

.udm-table th:nth-child(4),
.udm-table td:nth-child(4) {
    width: 15%;
}

.udm-table td.udm-eye-cell {
    width: 44px;
    padding: 0 !important;
    text-align: center;
    vertical-align: middle;
}
</style>
